// Generation jobs (contracts/studio.md §4): status, counts, tokens and cost, who asked; refreshed every
// ~3 s while any job on the page is queued or running, idle otherwise. Cancel for editor+.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useStudioJobs } from "../../api/studio";
import { JOB_STATUSES, type JobStatus } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, EmptyState, PageHeader, Segmented } from "../../components/ui";
import { isActiveJob } from "../../lib/studio";
import { BackLink, RoleNote, StudioError } from "./components";
import { JobsTable, LiveBadge } from "./jobs";
import s from "./studio.module.css";

const PAGE = 25;

export function StudioJobsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canEdit = useCan("studio.edit");
  const raw = params.get("status");
  const status = (JOB_STATUSES as readonly string[]).includes(raw ?? "") ? (raw as JobStatus) : null;
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [pageFor, setPageFor] = useState(status);
  if (pageFor !== status) {
    setPageFor(status);
    setCursors([null]);
  }
  const cursor = cursors[cursors.length - 1] ?? null;
  const q = useStudioJobs(status, cursor, PAGE);
  const live = Boolean(q.data?.items.some((j) => isActiveJob(j.status)));

  return (
    <>
      <PageHeader
        title="Jobs"
        meta={
          <>
            <BackLink to="/studio">Studio</BackLink>
            <span>Procedural, Claude and curated-import jobs, newest first.</span>
          </>
        }
        actions={
          <>
            <RoleNote />
            <LiveBadge live={live} />
            {canEdit ? (
              <Button variant="primary" onClick={() => navigate("/studio/new")}>
                New job
              </Button>
            ) : null}
          </>
        }
      />
      <div className={s.toolbar} style={{ marginTop: 0 }}>
        <Segmented<string>
          label="Job status"
          value={status ?? "all"}
          onChange={(v) => setParams(v === "all" ? {} : { status: v }, { replace: true })}
          options={[{ value: "all", label: "All" }, ...JOB_STATUSES.map((st) => ({ value: st, label: st }))]}
        />
      </div>
      {q.isError ? (
        <StudioError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={tableStyles.wrap} aria-busy={q.isFetching && !q.isRefetching}>
            <JobsTable jobs={q.data?.items} loading={q.isPending} rows={8} />
            {q.data && q.data.items.length === 0 ? (
              <EmptyState title={status ? `No ${status} jobs` : "No jobs yet"}>{canEdit ? "Queue one with New job." : "Editors queue jobs from New job."}</EmptyState>
            ) : null}
          </div>
          <div className={tableStyles.footer}>
            <span className="mono">
              Page {cursors.length}
              {q.data ? ` · ${q.data.items.length} jobs` : ""}
            </span>
            <div className={tableStyles.pager}>
              <Button size="sm" variant="ghost" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                ← Newer
              </Button>
              <Button size="sm" variant="ghost" disabled={!q.data?.nextCursor || q.isPlaceholderData} onClick={() => q.data?.nextCursor && setCursors((c) => [...c, q.data.nextCursor])}>
                Older →
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
